import { useState, useEffect } from "react";
import Header from "../../commonComponents/header/Header";
import arrowImg from "../../../assets/images/arrow-white-icon.svg";
import allSetImg from "../../../assets/images/nh_consumer_all_set.svg";
import { useNavigate } from "react-router-dom";
import LoadingDialog from "../../commonComponents/loader/LoadingDialog";
import CommonSnackbar from "../../commonComponents/snackbar/CommonSnackbar";

import { apiService } from "../../../services/apiService";

const ConnectionRestored = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState(null);

  // "camera" when arriving from a camera-block recovery,
  // "windowMgmt" when arriving from a window-management-block recovery,
  // "technical" when arriving after the interview socket dropped before EOI
  // (candidate tapped Continue on the technical-issue screen),
  // "network" otherwise.
  // NOTE: intentionally NOT cleared on mount — Chrome may show a "Reload to
  // apply settings" banner after the user re-grants a permission, and clicking
  // it hard-refreshes the page. We clear the flag only after Proceed is tapped.
  const restoredFrom = sessionStorage.getItem("restoredFrom") || "network";
  const isCameraRestore = restoredFrom === "camera";
  const isWindowMgmtRestore = restoredFrom === "windowMgmt";
  const isTechnicalRestore = restoredFrom === "technical";

  useEffect(() => {
    const controller = new AbortController();

    return () => {
      controller.abort(); // Safari-safe cleanup
    };
  }, []);

  const handleProceed = () => {
    // Clear now — user is actively proceeding, so a reload from here onwards
    // should correctly show the default "network" copy on any future visit.
    sessionStorage.removeItem("restoredFrom");

    const isMobileProctoring =
      sessionStorage.getItem("mobileProctoringScreenshot") === "true";
    const isInterviewAlreadyCompleted =
      sessionStorage.getItem("interviewAlreadyCompleted") === "true";
    if (isMobileProctoring && !isInterviewAlreadyCompleted) {
      navigate("/authentication", {
        state: { redirectFrom: "restored" },
      });
    } else {
      navigate("/assessment", {
        state: { redirectFrom: "restored" },
      });
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-white overflow-x-hidden px-4 pt-4">
      <Header />

      {loading && (
        <LoadingDialog
          title="Restoring Connection"
          bottomMsg="Preparing your assessment..."
        />
      )}

      {snackbar && (
        <CommonSnackbar
          message={snackbar.message}
          type={snackbar.type}
          onClose={() => setSnackbar(null)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col md:flex-row items-center justify-center pt-5 max-w-9xl">
        {/* Left */}
        <div className="w-full md:w-1/2 flex justify-center items-center pl-[34px] pr-[50px]">
          <img
            src={allSetImg}
            alt="connection-restored"
            className="object-contain w-[100%] h-[100%] rounded-3xl"
          />
        </div>

        {/* Divider */}
        <div className="hidden md:block self-stretch w-[1px] bg-dividerColor mx-1 my-[5%]" />

        {/* Right */}
        <div className="w-full md:w-1/2 flex flex-col justify-between px-5">
          <div>
            <h2 className="font-semibold text-fontColor mb-2 text-overFontSize">
              {isCameraRestore
                ? "Camera Access Restored"
                : isWindowMgmtRestore
                  ? "Window Management Restored"
                  : isTechnicalRestore
                    ? "You're All Set"
                    : "Connection Restored"}
            </h2>

            <p className="text-fontColor mb-6 leading-relaxed text-primaryFontSize">
              {isCameraRestore
                ? "Camera permission has been granted."
                : isWindowMgmtRestore
                  ? "Window Management permission has been re-enabled."
                  : isTechnicalRestore
                    ? "We ran into a brief technical issue, but it's been resolved."
                    : "Your internet connection is back."}
            </p>

            <p className="text-fontColor leading-relaxed text-primaryFontSize">
              Good news — your progress has been saved. You can continue the
              assessment from the exact point where it was interrupted. Please
              click <span className="font-medium">"Proceed"</span> when you are
              ready.
            </p>
          </div>
        </div>
      </div>

      {/* Proceed Button */}
      <div className="mt-auto w-full flex justify-end pb-4">
        <button
          className="btn-size flex bg-btnBgColor items-center justify-center px-6 py-1.5 text-white mr-[36px]"
          onClick={handleProceed}
        >
          Proceed
          <img src={arrowImg} alt="arrow" className="ml-2" />
        </button>
      </div>

      {/* Footer */}
      <div className="w-full py-4 text-[10px] text-gray-600 text-center px-4">
        ©{" "}
        {new Date().getFullYear() > 2026
          ? `2026-${new Date().getFullYear()}`
          : "2026"}
        , Needles and Haystacks Technology Pvt Ltd.
      </div>
    </div>
  );
};

export default ConnectionRestored;
